import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Download, RotateCcw } from 'lucide-react';
import { securitySettingsService } from '../../../services/securitySettingsService';
import Button from '../../../components/ui/Button';

export default function SecuritySettingsMatrix({ 
  securityMatrix, 
  onSettingChange, 
  onResetToDefaults,
  loading,
  canEdit 
}) {
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);

  const features = securitySettingsService?.getAvailableFeatures();
  const featureDisplayNames = securitySettingsService?.getFeatureDisplayNames();

  const toggleFeatureExpand = (feature) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [feature]: !prev?.[feature]
    }));
  };

  const handleBulkToggle = (roleId, enable) => {
    features?.forEach(feature => {
      onSettingChange(roleId, feature, enable);
    });
  };

  const exportSettings = () => {
    const exportData = {
      exportedAt: new Date()?.toISOString(),
      roles: securityMatrix?.map(role => ({
        roleId: role?.roleId,
        roleName: role?.roleName,
        roleDisplayName: role?.roleDisplayName,
        hierarchyLevel: role?.hierarchyLevel,
        permissions: role?.permissions
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-settings-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const getRolePermissionSummary = (role) => {
    const total = features?.length || 0;
    const allowed = features?.filter(f => role?.permissions?.[f]?.isAllowed)?.length || 0;
    const percentage = Math.round((allowed / total) * 100);
    return { allowed, total, percentage };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            value={selectedRole || ''}
            onChange={(e) => setSelectedRole(e?.target?.value || null)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Roles</option>
            {securityMatrix?.map(role => (
              <option key={role?.roleId} value={role?.roleId}>
                {role?.roleDisplayName}
              </option>
            ))}
          </select>
          {selectedRole && canEdit && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkToggle(selectedRole, true)}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Enable All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkToggle(selectedRole, false)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Disable All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResetToDefaults(selectedRole)}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
            </>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={exportSettings}
        >
          <Download className="w-4 h-4 mr-2" />
          Export Settings
        </Button>
      </div>
      {/* Role Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {securityMatrix
          ?.filter(role => !selectedRole || role?.roleId === selectedRole)
          ?.map(role => {
            const summary = getRolePermissionSummary(role);
            return (
              <div
                key={role?.roleId}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{role?.roleDisplayName}</h3>
                    <p className="text-xs text-gray-500">Level {role?.hierarchyLevel}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-indigo-600">{summary?.percentage}%</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Enabled:</span>
                    <span className="font-medium text-green-600">{summary?.allowed}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Disabled:</span>
                    <span className="font-medium text-red-600">{summary?.total - summary?.allowed}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all"
                      style={{ width: `${summary?.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {/* Permission Matrix Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Feature
                </th>
                {securityMatrix
                  ?.filter(role => !selectedRole || role?.roleId === selectedRole)
                  ?.map(role => (
                    <th
                      key={role?.roleId}
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span>{role?.roleDisplayName}</span>
                        <span className="text-xs text-gray-400 normal-case">
                          Level {role?.hierarchyLevel}
                        </span>
                      </div>
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {features?.map(feature => {
                const isExpanded = expandedFeatures?.[feature];
                return (
                  <tr key={feature} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {featureDisplayNames?.[feature]}
                        </span>
                      </div>
                    </td>
                    {securityMatrix
                      ?.filter(role => !selectedRole || role?.roleId === selectedRole)
                      ?.map(role => {
                        const permission = role?.permissions?.[feature];
                        const isAllowed = permission?.isAllowed || false;
                        const isTenantOverride = permission?.isTenantOverride || false;

                        return (
                          <td
                            key={`${role?.roleId}-${feature}`}
                            className="px-6 py-4 whitespace-nowrap text-center"
                          >
                            {canEdit ? (
                              <div className="flex flex-col items-center gap-1">
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isAllowed}
                                    onChange={(e) =>
                                      onSettingChange(
                                        role?.roleId,
                                        feature,
                                        e?.target?.checked
                                      )
                                    }
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                                {isTenantOverride && (
                                  <span className="text-xs text-orange-600 font-medium flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Override
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                {isAllowed ? (
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-gray-300" />
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Legend */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-2">Security Settings Legend:</p>
            <ul className="space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span><strong>Enabled:</strong> Role has access to this feature</span>
              </li>
              <li className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-gray-400" />
                <span><strong>Disabled:</strong> Role cannot access this feature</span>
              </li>
              <li className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <span><strong>Override:</strong> Tenant-specific setting (overrides global)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}